// Generic two-pane detail shell for admin trace views: a scrolling main
// column (header, summary cards, tabs) beside a right sidebar. The sidebar is
// optionally collapsible via the shell's own toggle; consumers whose sidebar
// already owns its collapse (e.g. the agent-traces Admin Agent sidebar) pass
// `collapsible={false}` and render the sidebar directly in the slot.

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';

// Single source of truth for the detail shell layout classes. Kept exported so
// callers (and their layout-contract tests) can reference the exact strings.
export const DETAIL_TWO_PANE_SHELL_CLASS_NAME =
    'flex min-w-0 gap-4 p-6 lg:h-svh lg:max-h-svh lg:min-h-0 lg:overflow-hidden';
export const DETAIL_TWO_PANE_SCROLL_REGION_CLASS_NAME =
    'min-h-0 overflow-y-auto';

const MAIN_COLUMN_CLASS_NAME =
    'flex min-w-0 flex-1 flex-col gap-4 lg:min-h-0 lg:overflow-y-auto';

export function DetailTwoPaneShell(props: {
    readonly header: ReactNode;
    readonly summaryCards?: ReactNode;
    readonly tabs: ReactNode;
    readonly sidebar?: ReactNode;
    readonly collapsible?: boolean;
    readonly storageKeyPrefix?: string;
    readonly sidebarKey?: string;
}): ReactNode {
    const collapsible = props.collapsible ?? true;
    const storageKey =
        props.storageKeyPrefix !== undefined && props.sidebarKey !== undefined
            ? `${props.storageKeyPrefix}.${props.sidebarKey}`
            : null;
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        if (storageKey === null) {
            return;
        }
        setCollapsed(localStorage.getItem(storageKey) === 'collapsed');
    }, [storageKey]);

    function toggleCollapsed() {
        setCollapsed((current) => {
            const next = !current;
            if (storageKey !== null) {
                localStorage.setItem(
                    storageKey,
                    next ? 'collapsed' : 'expanded',
                );
            }
            return next;
        });
    }

    return (
        <div className={DETAIL_TWO_PANE_SHELL_CLASS_NAME}>
            <div className={MAIN_COLUMN_CLASS_NAME}>
                {props.header}
                {props.summaryCards}
                {props.tabs}
            </div>
            {props.sidebar !== undefined && props.sidebar !== null ? (
                collapsible ? (
                    <div className="flex flex-col">
                        <Button
                            variant="ghost"
                            size="sm"
                            aria-label={
                                collapsed
                                    ? 'Expand sidebar'
                                    : 'Collapse sidebar'
                            }
                            onClick={toggleCollapsed}
                        >
                            {collapsed ? (
                                <ChevronRight className="h-4 w-4" />
                            ) : (
                                <ChevronLeft className="h-4 w-4" />
                            )}
                        </Button>
                        {collapsed ? null : props.sidebar}
                    </div>
                ) : (
                    props.sidebar
                )
            ) : null}
        </div>
    );
}

export function DetailTwoPaneShellSkeleton(): ReactNode {
    return (
        <div className={DETAIL_TWO_PANE_SHELL_CLASS_NAME}>
            <div className={MAIN_COLUMN_CLASS_NAME}>
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        </div>
    );
}

export function DetailTwoPaneShellNotFound(props: {
    readonly message: string;
}): ReactNode {
    return (
        <div className="text-muted-foreground flex min-h-svh items-center justify-center p-6">
            {props.message}
        </div>
    );
}
